import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { bookMeal, cancelBooking, IncompleteProfileError } from '../services/bookingService';
import { getMealAddress } from '../services/mealService';
import { checkInGuest } from '../Auth/host/hostService';
import { formatLongDateTime } from '../utils/dateFormat';
import './MealDetails.css';

const MealSummary = () => {
  const { mealId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [meal, setMeal] = useState(null);
  const [host, setHost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [booking, setBooking] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [hasBooked, setHasBooked] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [guests, setGuests] = useState([]);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [address, setAddress] = useState(null);

  const getAlcoholLabel = (option) => {
    const labels = {
      none: 'No alcohol',
      included: 'Alcohol included',
      byob: 'BYOB (Bring your own)',
    };
    return labels[option] || option;
  };

  useEffect(() => {
    const fetchMealAndHost = async () => {
      try {
        const mealDoc = await getDoc(doc(db, 'meals', mealId));
        if (mealDoc.exists()) {
          const mealData = mealDoc.data();
          setMeal(mealData);

          if (mealData.hostId) {
            const hostDoc = await getDoc(doc(db, 'users', mealData.hostId));
            if (hostDoc.exists()) {
              setHost(hostDoc.data());
            }

            // Check if current user already has a booking for this meal
            if (currentUser?.uid && currentUser.uid !== mealData.hostId) {
              const userBookingQuery = query(
                collection(db, 'bookings'),
                where('mealId', '==', mealId),
                where('guestId', '==', currentUser.uid),
                where('status', '==', 'confirmed')
              );
              const userBookingSnapshot = await getDocs(userBookingQuery);
              if (!userBookingSnapshot.empty) {
                setHasBooked(true);
                setBookingId(userBookingSnapshot.docs[0].id);
              }
            }

            if (currentUser?.uid === mealData.hostId) {
              const bookingsQuery = query(
                collection(db, 'bookings'),
                where('mealId', '==', mealId),
                where('hostId', '==', currentUser.uid),
                where('status', '==', 'confirmed')
              );
              const bookingsSnapshot = await getDocs(bookingsQuery);
              // Name/avatar are denormalized onto the booking doc at
              // booking time (F-13) -- no per-guest users/{guestId} read
              // needed for those. Phone/email are read live from each
              // guest's private contact subdoc (F-07): the host is
              // authorized because a contactGrants doc exists for this
              // confirmed booking pair.
              const guestProfiles = await Promise.all(
                bookingsSnapshot.docs.map(async (bookingDoc) => {
                  const bookingData = bookingDoc.data();
                  const guestId = bookingData.guestId;
                  const contactDoc = await getDoc(doc(db, 'users', guestId, 'private', 'contact'));
                  const contact = contactDoc.exists() ? contactDoc.data() : {};
                  return {
                    id: guestId,
                    bookingId: bookingDoc.id,
                    userAttendedMeal: bookingData.userAttendedMeal || false,
                    displayName: bookingData.guestDisplayName ||
                      `${bookingData.guestFirstName || ''} ${bookingData.guestLastName || ''}`.trim() ||
                      'Guest',
                    profileImage: bookingData.guestProfileImage,
                    email: contact.userEmail,
                    phoneNumber: contact.phoneNumber,
                  };
                })
              );
              setGuests(guestProfiles);
            }
          }
        } else {
          setError('Meal not found');
        }
      } catch (err) {
        console.error('Error fetching meal:', err);
        setError('Error loading meal details');
      } finally {
        setLoading(false);
      }
    };

    if (mealId) {
      fetchMealAndHost();
    }
  }, [mealId, currentUser]);

  // Address is only ever reachable through the getMealAddress callable
  // (F-06): the host always qualifies, a guest only once hasBooked flips
  // true for this meal.
  useEffect(() => {
    const isHost = currentUser && meal && currentUser.uid === meal.hostId;
    if (!mealId || !currentUser || !(isHost || hasBooked)) return;

    getMealAddress(mealId)
      .then(setAddress)
      .catch((err) => console.error('Error fetching meal address:', err));
  }, [mealId, currentUser, meal, hasBooked]);

  const handleBookMeal = async () => {
    if (!currentUser || !meal) return;

    setBooking(true);
    setBookingError(null);

    try {
      const result = await bookMeal({
        mealId,
        guestId: currentUser.uid,
        hostId: meal.hostId,
      });
      setMeal(prev => ({
        ...prev,
        currentGuests: (prev.currentGuests || 0) + 1,
      }));
      setHasBooked(true);
      setBookingSuccess(true);
      setBookingId(result.id);
    } catch (err) {
      if (err instanceof IncompleteProfileError) {
        setBookingError({ message: err.message, profileLink: err.profileLink });
      } else {
        setBookingError({ message: err.message });
      }
    } finally {
      setBooking(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!bookingId) return;

    setCancelling(true);
    setBookingError(null);

    try {
      await cancelBooking(bookingId);
      setMeal(prev => ({
        ...prev,
        currentGuests: Math.max(0, (prev.currentGuests || 0) - 1),
      }));
      setHasBooked(false);
      setBookingSuccess(false);
      setBookingId(null);
    } catch (err) {
      setBookingError({ message: err.message });
    } finally {
      setCancelling(false);
    }
  };

  const handleCheckIn = async (bookingId, guestId) => {
    try {
      await checkInGuest(bookingId, currentUser.uid);
      setGuests(prevGuests =>
        prevGuests.map(guest =>
          guest.id === guestId ? { ...guest, userAttendedMeal: true } : guest
        )
      );
    } catch (err) {
      console.error('Error checking in guest:', err);
      setBookingError({ message: err.message });
    }
  };

  const handleShareMeal = async () => {
    const shareUrl = `${window.location.origin}/meal-summary/${mealId}`;
    const shareData = {
      title: meal?.title || 'Join me for a meal!',
      text: `You're invited to ${meal?.title || 'a meal'} on SatSat!`,
      url: shareUrl,
    };

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 3000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 3000);
    }
  };

  if (loading) {
    return (
      <div className="meal-details-container">
        <div className="meal-details-card">
          <p className="loading-text">Loading meal details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="meal-details-container">
        <div className="meal-details-card">
          <p className="error-text">{error}</p>
          <button className="back-button" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const isHost = currentUser && meal && currentUser.uid === meal.hostId;
  const spotsRemaining = meal ? meal.maxGuests - (meal.currentGuests || 0) : 0;

  return (
    <div className="meal-details-container">
      {bookingSuccess && (
        <div className="booking-success-message">
          Your spot has been reserved! We look forward to seeing you at this meal.
        </div>
      )}
      {bookingError && (
        <p className="error-text">
          {bookingError.message}
          {bookingError.profileLink && (
            <> <Link to={bookingError.profileLink} className="error-link">Complete your profile</Link></>
          )}
        </p>
      )}

      <div className="meal-details-card">
        {meal.photoUrl && (
          <div className="meal-image-container">
            <img src={meal.photoUrl} alt={meal.title} className="meal-image" />
          </div>
        )}

        <div className="meal-header">
          <h1 className="meal-title">{meal.title}</h1>
          <span className={`meal-status status-${meal.status}`}>{meal.status}</span>
        </div>

        <p className="meal-cuisine">{meal.cuisineType} Cuisine</p>

        <div className="meal-datetime">
          <span className="datetime-icon">&#x1F4C5;</span>
          <span>{formatLongDateTime(meal.dateTime)}</span>
        </div>

        <section className="meal-section">
          <h2 className="section-title">About This Meal</h2>
          <p className="meal-description">{meal.description}</p>
        </section>

        {meal.menu && (
          <section className="meal-section">
            <h2 className="section-title">Menu</h2>
            <p className="meal-menu">{meal.menu}</p>
          </section>
        )}

        <section className="meal-section">
          <h2 className="section-title">Details</h2>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">Location</span>
              <span className="detail-value">
                {address
                  ? `${address.street}${address.unitNumber ? `, ${address.unitNumber}` : ''}, ${address.city}, ${address.state} ${address.zipCode}`
                  : (meal.neighborhood || 'Shown after booking')}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Spots Available</span>
              <span className="detail-value">
                {spotsRemaining} of {meal.maxGuests} spots remaining
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Alcohol</span>
              <span className="detail-value">{getAlcoholLabel(meal.alcoholOption)}</span>
            </div>
          </div>
        </section>

        {meal.dietaryAccommodations && meal.dietaryAccommodations.length > 0 && (
          <section className="meal-section">
            <h2 className="section-title">Dietary Accommodations</h2>
            <div className="dietary-tags">
              {meal.dietaryAccommodations.map((diet, index) => (
                <span key={index} className="dietary-tag">
                  {diet}
                </span>
              ))}
            </div>
          </section>
        )}

        {meal.specialNotes && (
          <section className="meal-section">
            <h2 className="section-title">Special Notes</h2>
            <p className="special-notes">{meal.specialNotes}</p>
          </section>
        )}

        {host && (
          <section className="meal-section host-section">
            <h2 className="section-title">Your Host</h2>
            <div className="host-info">
              {host.userProfile?.profileImage && (
                <img src={host.userProfile.profileImage} alt={host.userProfile?.firstName} className="host-avatar" />
              )}
              <div className="host-details">
                <span className="host-name">
                  {[host.userProfile?.firstName, host.userProfile?.lastName].filter(Boolean).join(' ') || 'Host'}
                </span>
              </div>
            </div>
          </section>
        )}

        <div className="meal-actions">
          {isHost ? (
            <button className="edit-button" onClick={() => navigate(`/edit-listing/${mealId}`)}>
              Edit Listing
            </button>
          ) : hasBooked ? (
            <>
              <button className="reserve-button reserved" disabled>
                Spot Reserved!
              </button>
              <button className="cancel-button" onClick={handleCancelBooking} disabled={cancelling}>
                {cancelling ? 'Cancelling...' : 'Cancel Reservation'}
              </button>
            </>
          ) : (
            spotsRemaining > 0 && (
              <button className="reserve-button" onClick={handleBookMeal} disabled={booking}>
                {booking ? 'Booking...' : 'Reserve a Spot'}
              </button>
            )
          )}
          <button className="share-button" onClick={handleShareMeal}>
            {shareSuccess ? 'Link Copied!' : 'Share This Meal'}
          </button>
          <button className="back-button" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      </div>

      {isHost && (
        <div className="guest-list-section">
          <h2 className="section-title">Guest List</h2>
          {guests.length === 0 ? (
            <p className="guest-list-empty">No guests have booked yet.</p>
          ) : (
            <div className="guest-list">
              {guests.map((guest) => (
                <div key={guest.id} className="guest-tile">
                  <div className="guest-avatar">
                    {guest.profileImage ? (
                      <img src={guest.profileImage} alt={guest.displayName} />
                    ) : (
                      <div className="guest-avatar-placeholder">
                        {guest.displayName?.charAt(0).toUpperCase() || 'G'}
                      </div>
                    )}
                  </div>
                  <div className="guest-info">
                    <span className="guest-name">{guest.displayName}</span>
                    {guest.email && (
                      <a href={`mailto:${guest.email}`} className="guest-email">{guest.email}</a>
                    )}
                    {guest.phoneNumber && (
                      <a href={`tel:${guest.phoneNumber}`} className="guest-phone">{guest.phoneNumber}</a>
                    )}
                  </div>
                  <button
                    className={`check-in-button ${guest.userAttendedMeal ? 'checked-in' : ''}`}
                    onClick={() => handleCheckIn(guest.bookingId, guest.id)}
                    disabled={guest.userAttendedMeal}
                  >
                    {guest.userAttendedMeal ? 'Checked In' : 'Check In'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MealSummary;
